import { CLARITY_DASHBOARD_TABS, type ClarityDashboardTab } from './constants/clarity-dashboard-tabs.constant';
import { isClarityUuid } from './is-clarity-uuid.util';
import type { ClarityWorkspaceLocation } from './models/clarity-workspace-location.model';

/** The tab every missing or unknown segment falls back to. */
const DEFAULT_TAB: ClarityDashboardTab = 'pulse';

function validUuid(value: string | null | undefined): string | null {
	return isClarityUuid(value) ? value : null;
}

function validPositiveInt(value: string | null | undefined): number | null {
	if (!value) {
		return null;
	}

	const n = Number(value);

	if (!Number.isInteger(n) || n < 1) {
		return null;
	}

	return n;
}

function validTab(value: string | undefined): ClarityDashboardTab {
	const tab = CLARITY_DASHBOARD_TABS.find(candidate => candidate === value);

	return tab ?? DEFAULT_TAB;
}

/** Ported from Clarity `apps/web/src/shared/utils/workspaceUrl.ts` (commit `f9217bb5a`). */
/**
 * Parses route params and a query into a validated workspace location — brain's
 * `parseWorkspaceLocation`, unchanged.
 *
 * Every value is untrusted input (their TR-012). Canonicalisation: a missing or unknown tab → `pulse`;
 * `page` without `doc` → null; both `doc` and `file` → `file` wins and `doc` is dropped.
 */
export function parseClarityWorkspaceLocation(
	params: Readonly<Record<string, string | undefined>>,
	search: URLSearchParams,
): ClarityWorkspaceLocation {
	const domainId = validUuid(params.domainId) ?? null;
	const tab = validTab(params.tab);

	let doc = validUuid(search.get('doc'));
	const file = validUuid(search.get('file'));
	let page = validPositiveInt(search.get('page'));
	const oi = validPositiveInt(search.get('oi'));
	const action = validUuid(search.get('action'));

	// Canonicalization: file wins over doc
	if (file && doc) {
		doc = null;
	}

	// Canonicalization: page without doc is meaningless
	if (!doc) {
		page = null;
	}

	return { domainId, tab, doc, page, file, oi, action };
}
